import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const milestones = [
  { year: '2007', event: 'Sweet Celebrations founded in Abilene, Texas' },
  { year: '2010', event: 'Named top custom bakery in Abilene by local community' },
  { year: '2014', event: 'Moved to Lubbock, Texas and quickly gained a loyal following' },
  { year: '2018', event: 'Expanded into cake decorating classes and baking supplies' },
  { year: 'Today', event: 'A beloved Lubbock institution serving hundreds of families each year' },
];

const values = [
  {
    icon: 'ri-heart-3-line',
    title: 'Made with Love',
    desc: 'Every order receives the same care and attention as if it were for our own family celebration.',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Creative Vision',
    desc: 'We take your ideas, dreams, and inspiration and transform them into an edible work of art.',
  },
  {
    icon: 'ri-medal-line',
    title: 'Quality First',
    desc: 'We use only premium ingredients because your celebration deserves nothing less than perfection.',
  },
  {
    icon: 'ri-community-line',
    title: 'Community Roots',
    desc: 'Deeply proud of our Lubbock community — we celebrate the moments that matter most to you.',
  },
  {
    icon: 'ri-sparkling-line',
    title: 'Attention to Detail',
    desc: 'From the first consultation to final delivery, no detail is too small for Sweet Celebrations.',
  },
  {
    icon: 'ri-time-line',
    title: '17+ Years Experience',
    desc: 'Over a decade and a half of baking experience means your event is in the most capable hands.',
  },
];

export default function AboutPage() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = heroRef.current;
    if (!el) return;
    setTimeout(() => {
      el.classList.remove('opacity-0', 'translate-y-6');
      el.classList.add('opacity-100', 'translate-y-0');
    }, 100);
  }, []);

  return (
    <div className="min-h-screen bg-cream-50 font-sans">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-charcoal-800">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/7cecc7e0fe244b5002ffa3e7de924ff0.jpg"
            alt="Sweet Celebrations bakery"
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/80 to-charcoal-900/40" />
        </div>
        <div
          ref={heroRef}
          className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center transition-all duration-1000 ease-out opacity-0 translate-y-6"
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-10 bg-gold-400" />
            <span className="text-gold-400 text-xs uppercase tracking-[0.3em] font-sans font-semibold">Our Story</span>
            <div className="h-px w-10 bg-gold-400" />
          </div>
          <h1 className="font-serif text-5xl md:text-6xl text-white mb-6 leading-tight">
            About <em className="italic text-blush-300">Sweet Celebrations</em>
          </h1>
          <p className="text-cream-200 text-lg font-sans font-light leading-relaxed max-w-2xl mx-auto">
            Baking beautiful memories in Lubbock, Texas since 2007. A story of creativity, community, and cake.
          </p>
        </div>
      </section>

      {/* Main Story */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-3">
                <div className="h-px w-10 bg-gold-500" />
                <span className="text-gold-500 text-xs uppercase tracking-[0.25em] font-sans font-semibold">Since 2007</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-charcoal-800 leading-tight">
                Where Every Cake<br />Tells a <em className="italic text-blush-500">Story</em>
              </h2>
              <div className="flex flex-col gap-5 text-warm-gray text-base leading-[1.9] font-sans font-light">
                <p>
                  Sweet Celebrations was born in 2007 from a genuine passion for bringing joy through beautiful, delicious baked creations. What started as simple, heartfelt cakes for family and friends quickly grew into something the entire community wanted to be part of.
                </p>
                <p>
                  After establishing a sterling reputation in Abilene, Texas — where Sweet Celebrations became one of the most sought-after custom bakeries in the region — the business made an exciting move to Lubbock, the Heart of the South Plains. The Lubbock community embraced us immediately, and we&apos;ve never looked back.
                </p>
                <p>
                  Years of serving Lubbock families, couples, and businesses has only deepened our love for this community. We&apos;ve had the honor of being part of thousands of celebrations — from intimate birthday parties to grand weddings — each one unique and deeply personal.
                </p>
                <p>
                  As our community grew, so did our offerings. We now provide cake decorating classes and baking supplies, giving aspiring bakers the skills and tools to bring their own sweet visions to life.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl overflow-hidden h-72 col-span-2">
                <img
                  src="https://readdy.ai/api/search-image?query=beautiful%20handcrafted%20custom%20cakes%20displayed%20in%20elegant%20bakery%20showcase%20blush%20pink%20and%20gold%20color%20theme%2C%20professional%20patisserie%20display%2C%20Lubbock%20Texas%20bakery%2C%20warm%20inviting%20atmosphere%2C%20stunning%20dessert%20presentation%2C%20artisan%20crafted%2C%20award%20winning%20bakery%20quality&width=800&height=400&seq=about-main001&orientation=landscape"
                  alt="Sweet Celebrations custom cakes display"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden h-44">
                <img
                  src="https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20intricate%20cake%20decoration%20sugar%20roses%20fondant%20flowers%20and%20gold%20accents%20on%20wedding%20cake%2C%20professional%20bakery%20craftsmanship%2C%20artisan%20detail%20work%2C%20elegant%20and%20sophisticated%2C%20luxury%20patisserie&width=400&height=300&seq=about-detail001&orientation=landscape"
                  alt="Custom cake detail"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden h-44">
                <img
                  src="https://readdy.ai/api/search-image?query=decorated%20sugar%20cookies%20and%20pastries%20arranged%20beautifully%20on%20cream%20marble%20surface%20with%20blush%20pink%20and%20gold%20color%20scheme%2C%20professional%20food%20photography%2C%20elegant%20bakery%20treats%2C%20Lubbock%20Texas%20small%20business%2C%20artisan%20baking&width=400&height=300&seq=about-detail002&orientation=landscape"
                  alt="Decorated cookies and pastries"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-warm-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-gold-500" />
              <span className="text-gold-500 text-xs uppercase tracking-[0.25em] font-sans font-semibold">What We Stand For</span>
              <div className="h-px w-10 bg-gold-500" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal-800 mb-4">
              Our <em className="italic text-blush-500">Values</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-8 border border-warm-200 hover:border-blush-300 hover:scale-[1.02] transition-all duration-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blush-100 text-blush-500 mb-5">
                  <i className={`${v.icon} text-xl`} />
                </div>
                <h3 className="font-serif text-xl text-charcoal-800 mb-3">{v.title}</h3>
                <p className="text-warm-gray text-sm font-sans leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-gold-500" />
              <span className="text-gold-500 text-xs uppercase tracking-[0.25em] font-sans font-semibold">Our Journey</span>
              <div className="h-px w-10 bg-gold-500" />
            </div>
            <h2 className="font-serif text-4xl text-charcoal-800">
              A Sweet <em className="italic text-blush-500">History</em>
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-blush-200" />
            <div className="flex flex-col gap-8">
              {milestones.map((m, i) => (
                <div key={i} className="flex items-start gap-6 pl-2">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blush-500 text-white z-10 relative">
                      <span className="font-sans font-bold text-xs">{m.year === 'Today' ? '★' : m.year.slice(2)}</span>
                    </div>
                  </div>
                  <div className="pt-3">
                    <span className="text-gold-600 font-sans font-bold text-sm">{m.year}</span>
                    <p className="text-charcoal-800 font-sans text-base mt-1 leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blush-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal-800 mb-6">
            Ready to Create Something<br /><em className="italic text-blush-500">Unforgettable?</em>
          </h2>
          <p className="text-warm-gray text-base font-sans font-light leading-relaxed mb-8">
            Let&apos;s bring your vision to life. Contact Sweet Celebrations today and let&apos;s start designing your dream dessert.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/custom-orders"
              className="inline-flex items-center gap-2 bg-blush-500 hover:bg-blush-600 text-white px-8 py-4 rounded-full font-sans font-semibold text-sm tracking-wide whitespace-nowrap transition-all duration-200 hover:scale-105 cursor-pointer"
            >
              Request a Custom Order
              <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="https://www.facebook.com/scbakeacake"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 border border-charcoal-800 text-charcoal-800 hover:bg-charcoal-800 hover:text-white px-8 py-4 rounded-full font-sans font-semibold text-sm tracking-wide whitespace-nowrap transition-all duration-200 cursor-pointer"
            >
              <i className="ri-facebook-fill" />
              Find Us on Facebook
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
